import CSS from 'csstype';
import { isEmpty } from 'lodash';

import { getBidderLabelsList } from '~/src/dash/api/filters/bidderLabels';
import { getSmallBiddersList } from '~/src/dash/api/filters/bidders';
import { getFilteredDemandSourcesList } from '~/src/dash/api/filters/demandSources';
import { getProviderNames, getSegmentTypes } from '~/src/dash/api/liveramp-marketplace';
import {
  BiddersPillIcon,
  ChannelsPillIcon,
  ClockPillIcon,
  DataLinePillIcon,
  DataPillIcon,
  DealsPillIcon,
  DeviceTypePillIcon,
  RoutePillIcon,
  StatusPillIcon,
  TiersPillIcon,
  TopSortPillIcon,
} from '~/src/dash/components/icons/pill-icons';
import { SortByMode } from '~/src/dash/components/material-search-dropdown-multi-list';
import { genTimezoneList } from '~/src/dash/globals/timezone';
import { ISessionState } from '~/src/dash/reducers/session-reducers';
import { IUserState } from '~/src/dash/reducers/user-reducers';
import { IListItem, IMenuList } from '~/src/dash/types/menuList';
import { generateCountriesMenuList } from '~/src/dash/utils/countries';
import { createAllDeviceTypesMenuList } from '~/src/dash/utils/devices';
import {
  genAuctionRequestersList,
  genClusters,
  genDomains,
  genPredictiveSmoothingCohortsList,
  genRegions,
} from '~/src/dash/utils/graph';
import { isPhiloPublisher, isSamsungNAPublisher, isTestPublisher } from '~/src/dash/utils/ui';
import {
  generateDemandSourcesList,
  getBidderGroupsList,
  getChannelGroupsList,
  getDeals,
  getPublicaPublishersList,
  getPublishersList,
  getSmallSitesList,
} from '~/src/dash/views/common/dashboards-consts';
import { IActiveFilters } from '~/src/dash/views/dashboards/DashboardHome/common/common';
import { generatePartnersList } from '~/src/dash/views/segments/audience/common';
import { TKey } from '~/src/dash/publicaUI';

export interface IFilter {
  menuTitle: string;
  list: Record<string, IListItem>;
}

export type IFiltersState = Record<string, IFilter>;

const generateStatusList = () => {
  const statusList: IMenuList = {};
  // show all is added automatically at 0
  const options = ['Show All', 'Active', 'Inactive'];
  // we save id = 0 for default show all
  options.forEach((option, i) => {
    statusList[`${i}`] = {
      ID: `${i}`,
      title: option,
      selected: false,
    };
  });
  return statusList;
};

const getBidderTierList = async (
  session: ISessionState,
  _user: IUserState,
): Promise<Record<string, IMenuList>> => {
  return new Promise((resolve, _reject) => {
    const bidderTierList: IMenuList = {};
    if (
      isSamsungNAPublisher(session.publisher.ID) ||
      isPhiloPublisher(session.publisher.ID) ||
      isTestPublisher(session.publisher.ID)
    ) {
      bidderTierList[0] = {
        ID: `${0}`,
        title: `Tier 0`,
        selected: false,
      };
    }
    for (let i = 1; i <= 10; i++) {
      bidderTierList[i] = {
        ID: `${i}`,
        title: `Tier ${i}`,
        selected: false,
      };
    }
    resolve({ 'Bidder Tiers': bidderTierList });
  });
};

export const EPriorities = {
  11: 0,
  10: 1,
  9: 2,
  8: 3,
  7: 4,
  6: 5,
  5: 6,
  4: 7,
  3: 8,
  2: 9,
  1: 10,
  0: 11,
} as Record<string, number>;

export const generatePriorityList = (publisherID: string) => {
  const priorityList: IMenuList = {};
  if (isSamsungNAPublisher(publisherID) || isTestPublisher(publisherID)) {
    priorityList[0] = {
      ID: '0',
      title: '0',
      selected: false,
    };
  }
  for (let i = 1; i <= 10; i++) {
    priorityList[i] = {
      ID: `${i}`,
      title: `${i}`,
      selected: false,
    };
  }
  return priorityList;
};

const generateLiverampStatusList = () => {
  const statusList: IMenuList = {};
  const options = ['All', 'Requested', 'Distributed'];
  options.forEach((option, i) => {
    statusList[`${i}`] = {
      ID: `${i}`,
      title: option,
      selected: false,
    };
  });
  return statusList;
};

const generateUseCaseList = () => {
  const useCaseList: IMenuList = {};
  const options = [
    'Digital Media Targeting',
    'Audience Extension',
    'Campaign Analytics',
    'Measurement And Analytics',
    'TV Media Targeting',
  ];

  options.forEach((option, _) => {
    useCaseList[option] = {
      ID: option,
      title: option,
      selected: false,
    };
  });
  return useCaseList;
};

const generateRequestedList = () => {
  const requesetdList: IMenuList = {};
  // show all is added automatically at 0
  const options = ['Show All', 'Requested', 'Not Requested'];
  // we save id = 0 for default show all
  options.forEach((option, i) => {
    requesetdList[`${i}`] = {
      ID: `${i}`,
      title: option,
      selected: false,
    };
  });
  return requesetdList;
};

export const hasPreselectedFilters = (
  filterConfig: ICustomFilterConfig | ICustomAPIFilterConfig,
) => {
  return Object.values(filterConfig).some(
    (filter) => filter.defaultSelected && !isEmpty(filter.defaultSelected),
  );
};

export const getFilterParams = (filters: IFiltersState, filterConfig: ICustomAPIFilterConfig) => {
  const filterParams: Record<string, any> = {};
  Object.keys(filterConfig).forEach((filterKey) => {
    const { queryKey, formatRequestValue } = filterConfig[filterKey];
    const selectedList = filters[filterKey] ? filters[filterKey].list : {};
    filterParams[queryKey] = formatRequestValue(selectedList);
  });
  return filterParams;
};

export const getDashboardFilterParams = (
  filters: Record<string, IMenuList>,
  filterConfig: ICustomAPIFilterConfig,
  activeFilters: IActiveFilters,
  isUsingBidderType?: boolean,
) => {
  const filterParams: Record<string, any> = {};
  Object.entries(filterConfig).forEach(([filterKey, value]) => {
    const { queryKey, formatRequestValue, category } = value;
    const selectedList = filters[filterKey] || ({} as IMenuList);
    if (!category || activeFilters[category] === filterKey) {
      if (filterKey === 'Filtered Demand Sources' && isUsingBidderType) {
        filterParams.bidder_ids = formatSelectedCustomDemandSources(selectedList);
      } else {
        filterParams[queryKey] = formatRequestValue(selectedList);
      }
    }
  });
  return filterParams;
};

export const getInitialDashboardFilterParams = (
  filterConfig: ICustomAPIFilterConfig,
  activeFilters: IActiveFilters,
  isUsingBidderType?: boolean,
) => {
  const filterParams: Record<string, any> = {};
  Object.entries(filterConfig).forEach(([filterKey, value]) => {
    const {
      queryKey,
      formatRequestValue,
      category,
      defaultSelected,
      isGroup,
      defaultGroupSelected,
    } = value;
    if (!category || (activeFilters[category] === filterKey && !isEmpty(defaultSelected))) {
      if (isGroup) {
        if (filterKey === 'Filtered Demand Sources' && isUsingBidderType) {
          filterParams.bidder_ids = formatSelectedByJoiningIDsWithCommas(
            defaultGroupSelected || [],
          );
        } else {
          filterParams[queryKey] = formatRequestValue(defaultSelected || []);
        }
      } else {
        filterParams[queryKey] = formatRequestValue(defaultSelected || []);
      }
    }
  });
  return filterParams;
};

export const getMobileFilterParams = (
  filters: Record<string, IMenuList>,
  filterConfig: ICustomAPIFilterConfig,
  activeFilters: IActiveFilters,
) => {
  const filterParams: Record<string, any> = {};
  Object.entries(filterConfig).forEach(([filterKey, value]) => {
    const { queryKey, formatRequestValue, category } = value;
    const selectedList = filters[filterKey] || ({} as IMenuList);
    if (!category || activeFilters[category] === filterKey) {
      filterParams[queryKey] = formatRequestValue(selectedList);
    }
  });
  return filterParams;
};

const extractKeys = (selectedList: IMenuList | string[]) =>
  Array.isArray(selectedList) ? selectedList : Object.keys(selectedList);

// format for api requests
export const formatSelectedBidderTiers = (selectedList: IMenuList) => {
  if (isEmpty(selectedList)) {
    return [];
  }
  return Object.keys(selectedList).map((item) => EPriorities[item]);
};

export const formatSelectedToArrayOfStrings = (selectedList: IMenuList) => {
  if (isEmpty(selectedList)) {
    return [];
  }
  return Object.keys(selectedList).map((item) => item);
};

export const formatSelectedToArrayOfNumbers = (selectedList: IMenuList) => {
  if (isEmpty(selectedList)) {
    return [];
  }
  return Object.keys(selectedList).map((item) => Number(item));
};

export const formatSelectedToStringWithEscapes = (selectedList: IMenuList | string[]) => {
  if (isEmpty(selectedList)) {
    return '';
  }
  return extractKeys(selectedList)
    .map((item) => "'" + item + "'")
    .join(',');
};

export const formatSelectedByJoiningIDsWithCommas = (selectedList: IMenuList | string[]) => {
  return extractKeys(selectedList).join(',');
};

export const formatSelectedToUpperCaseStringWithCommasAndEscapes = (
  selectedList: IMenuList | string[],
) => {
  return extractKeys(selectedList)
    .map((key) => "'" + key.toUpperCase() + "'")
    .join(',');
};

export const formatSelectedBidderGroupIntoStringOfIDs = (selectedList: IMenuList | string[]) => {
  if (isEmpty(selectedList)) return '';
  if (Array.isArray(selectedList)) return selectedList.join(',');
  const selected = Object.values(selectedList)[0];
  return selected.bidderGroup?.BidderIDs || '';
};

export const formatSelectedChannelGroupIntoStringOfIDs = (selectedList: IMenuList | string[]) => {
  if (isEmpty(selectedList)) return '';
  if (Array.isArray(selectedList)) return selectedList.join(',');
  const selected = Object.values(selectedList)[0];
  return selected.channelGroup?.ChannelIDs || '';
};

export const formatSelectedRouteIntoStringOfSiteIDs = (selectedList: IMenuList | string[]) => {
  if (isEmpty(selectedList)) return '';
  if (Array.isArray(selectedList)) return selectedList.join(',');
  const selected = Object.values(selectedList)[0];
  return (selected.siteIDs || []).join(',');
};

export const formatSelectedDestinationsIntoStringOfSiteIDs = (
  selectedList: IMenuList | string[],
) => {
  if (isEmpty(selectedList)) return '';
  if (Array.isArray(selectedList)) return selectedList.join(',');
  const selectedSiteIDs: string[] = [];
  Object.values(selectedList).forEach((item) => {
    item.siteID && selectedSiteIDs.push(item.siteID?.toString());
  });
  return selectedSiteIDs.join(',');
};

export const formatSelectedCustomDemandSources = (selectedList: IMenuList | string[]) => {
  if (isEmpty(selectedList)) return '';
  const selectedBidderIDsMap: Record<string, boolean> = {};
  Object.values(selectedList).forEach((item) => {
    item.bidderIDs.forEach((bidderID: number) => {
      selectedBidderIDsMap[bidderID.toString()] = true;
    });
  });
  return Object.keys(selectedBidderIDsMap).join(',');
};

const EShowAllActiveInactive = {
  0: '', // SHOW ALL
  1: '2', // ACTIVE
  2: '1', // INACTIVE
} as Record<string, string>;
// works only if ID translates to the following... 0: All, 1: Active, 2: Inactive
export const formatSelectedAllActiveInactiveToEBoolString = (selectedList: IMenuList) => {
  if (isEmpty(selectedList)) {
    return '';
  }
  const selected = Object.values(selectedList);
  return EShowAllActiveInactive[selected[0].ID];
};

export interface IFilterPill {
  defaultSelected?: string[]; // array of ids that are selected
  defaultGroupSelected?: string[];
  formatRequest?: (selectedList: IMenuList) => any;
  getList?: (session: ISessionState, user?: IUserState) => Promise<any>; // function to retrieve list if session / user is needed
  handleFilterAccept: (menuTitle: string, list: IMenuList) => void;
  icon?: FilterPillIconKey; // icon info object
  isGroup?: boolean;
  isSingleSelect?: boolean;
  list?: IMenuList;
  menuTitle: FilterPillKey; // plural label
  paramKey: string; // key for api request
  singularString: string; // singular label
  sortMode?: SortByMode;
  tKey?: TKey;
}

export type FilterPillKey =
  | 'Auction Requesters'
  | 'Bidder Tiers'
  | 'Bidders'
  | 'Bidder Groups'
  | 'Bidder Labels'
  | 'Channels'
  | 'Channel Groups'
  | 'Clusters'
  | 'Domains'
  | 'Countries'
  | 'Demand Source'
  | 'Demand Sources'
  | 'Destinations'
  | 'Device Types'
  | 'Filtered Demand Sources'
  | 'Outbound Deals'
  | 'Segment Status'
  | 'Partners'
  | 'Priorities'
  | 'Publica Publishers'
  | 'Publishers'
  | 'Publisher Channels'
  | 'Regions'
  | 'Routes'
  | 'Status'
  | 'Timezones'
  | 'Providers'
  | 'Segment Type'
  | 'Use Cases'
  | 'IsRequested'
  | 'Predictive Smoothing Cohorts';

export type FilterCategory =
  | 'demand'
  | 'supply'
  | 'device'
  | 'deal'
  | 'publisher'
  | 'cluster'
  | 'domain'
  | 'region'
  | 'requester'
  | 'predictiveSmoothingCohorts';

interface ICustomFilterInfo {
  defaultSelected?: string[];
  filterFn: (item: Record<string, any>, props: any) => boolean; // true conditional to filter
  formatProps?: (list: IMenuList) => any; // use this to get the needed props for filterFn
  formatRequestValue?: (selectedList: IMenuList) => any;
  hasSelectAll?: boolean; // adds select all option if using material search dropdown multi
  queryKey?: string;
}

export interface ICustomFilterConfig {
  [key: string]: ICustomFilterInfo;
}

export interface ICustomAPIFilterInfo {
  defaultSelected?: string[];
  defaultGroupSelected?: string[];
  formatRequestValue: (selectedList: IMenuList | string[]) => any;
  hasSelectAll?: boolean; // adds select all option if using material search dropdown multi
  isGroup?: boolean; // selected ids ex. bidder groups
  isMultiSelectDisabled?: boolean; // disables multi select on material search dropdown multi
  isSubset?: boolean; // contains a filtered amount of a larger filter. ex: 'Routes' is a subset of 'Channels'
  queryKey: string;
  limit?: number; // adds a maximum limit to how many can be selected
  displayID?: boolean;
  category?: FilterCategory; // used if grouping together filters
  isSelectionRequired?: boolean; // cannot deselect a singular option or clear the filter
  isActiveStatusEnabled?: boolean; // true specifies it contains a meaningful active prop
  tKey?: TKey;
}

export interface ICustomAPIFilterConfig {
  [key: string]: ICustomAPIFilterInfo;
}

export const FILTER_PILLS = {
  // please maintain alphabetical order
  'Auction Requesters': {
    menuTitle: 'Auction Requesters',
    singularString: 'Auction Requester',
    paramKey: 'publica_auction_requesters',
    icon: 'data_line',
    list: genAuctionRequestersList(),
  },
  Bidders: {
    menuTitle: 'Bidders',
    singularString: 'Bidder',
    paramKey: 'bidder_ids',
    icon: 'bidders',
    getList: (session: ISessionState, user: IUserState) => getSmallBiddersList(session, user),
    tKey: 'Bidders',
  },
  'Bidder Groups': {
    menuTitle: 'Bidder Groups',
    singularString: 'Bidder Group',
    paramKey: 'bidder_group_ids',
    icon: 'bidders',
    getList: (session: ISessionState, user: IUserState) => getBidderGroupsList(session, user),
    isGroup: true,
    tKey: 'BidderGroups',
  },
  'Bidder Labels': {
    menuTitle: 'Bidder Labels',
    singularString: 'Bidder Label',
    paramKey: 'bidder_labels',
    icon: 'bidders',
    getList: (session: ISessionState, user: IUserState) => getBidderLabelsList(session, user),
    tKey: 'BidderLabels',
  },
  'Bidder Tiers': {
    menuTitle: 'Bidder Tiers',
    getList: (session: ISessionState, user: IUserState) => getBidderTierList(session, user),
    singularString: 'Bidder Tier',
    paramKey: 'bidder_tiers',
    sortMode: SortByMode.IdAsc,
    icon: 'tiers',
    tKey: 'BidderTiers',
  },
  Channels: {
    menuTitle: 'Channels',
    singularString: 'Channel',
    paramKey: 'site_ids',
    icon: 'channels',
    getList: (session: ISessionState, user: IUserState) => getSmallSitesList(session, user),
    tKey: 'Channels',
  },
  'Channel Groups': {
    menuTitle: 'Channel Groups',
    singularString: 'Channel Group',
    paramKey: 'channel_group_ids',
    icon: 'channels',
    getList: (session: ISessionState, user: IUserState) => getChannelGroupsList(session, user),
    isGroup: true,
    tKey: 'ChannelGroups',
  },
  Clusters: {
    menuTitle: 'Clusters',
    singularString: 'Cluster',
    paramKey: 'publica_clusters',
    icon: 'data_line',
    list: genClusters(),
  },
  Countries: {
    menuTitle: 'Countries',
    list: generateCountriesMenuList(),
    singularString: 'Country',
    paramKey: 'countries',
  },
  'Demand Sources': {
    menuTitle: 'Demand Sources',
    singularString: 'Demand Source',
    paramKey: 'biddercodes',
    icon: 'bidders',
    getList: (session: ISessionState, user: IUserState) => generateDemandSourcesList(session, user),
    tKey: 'DemandSources',
  },
  'Filtered Demand Sources': {
    menuTitle: 'Demand Sources',
    singularString: 'Demand Source',
    paramKey: 'biddercodes',
    icon: 'bidders',
    getList: (session: ISessionState, user: IUserState) =>
      getFilteredDemandSourcesList(session, user),
    tKey: 'DemandSources',
  },
  Destinations: {
    menuTitle: 'Destinations',
    list: {},
    singularString: 'Destination',
    paramKey: 'destinations',
    icon: 'route',
    tKey: 'Destinations',
  },
  Domains: {
    menuTitle: 'Domains',
    singularString: 'Domain',
    paramKey: 'publica_domains',
    icon: 'data_line',
    list: genDomains(),
  },
  'Device Types': {
    menuTitle: 'Device Types',
    list: createAllDeviceTypesMenuList(),
    singularString: 'Device Type',
    paramKey: 'devicetypes',
    icon: 'device_type',
    tKey: 'DeviceTypes',
  },
  'Outbound Deals': {
    menuTitle: 'Outbound Deals',
    singularString: 'Outbound Deal',
    paramKey: 'deals',
    icon: 'deals',
    getList: (session: ISessionState, user: IUserState) => getDeals(session, user),
    tKey: 'OutboundDeals',
  },
  'Segment Status': {
    menuTitle: 'Segment Status',
    list: generateLiverampStatusList(),
    paramKey: 'status',
    isSingleSelect: true,
  },
  Partners: {
    menuTitle: 'Partners',
    singularString: 'Partner',
    paramKey: 'partner',
    getList: (session: ISessionState) => generatePartnersList(session),
    icon: 'data',
  },
  'Predictive Smoothing Cohorts': {
    menuTitle: 'Predictive Smoothing Cohorts',
    singularString: 'PredictiveSmoothingCohorts',
    paramKey: 'predictive_smoothing_cohorts',
    isSingleSelect: true,
    list: genPredictiveSmoothingCohortsList(),
  },
  Priorities: {
    menuTitle: 'Priorities',
    list: {},
    singularString: 'Priority',
    paramKey: 'priority',
    icon: 'tiers',
  },
  Publishers: {
    menuTitle: 'Publishers',
    singularString: 'Publisher',
    paramKey: 'publisher_ids',
    getList: (session: ISessionState, user: IUserState) => getPublishersList(session, user),
    icon: 'data_line',
  },
  'Publica Publishers': {
    menuTitle: 'Publishers',
    singularString: 'Publisher',
    paramKey: 'publisher_ids',
    getList: (session: ISessionState, user: IUserState) => getPublicaPublishersList(session, user),
    icon: 'data_line',
  },
  Regions: {
    menuTitle: 'Regions',
    singularString: 'Region',
    paramKey: 'publica_regions',
    icon: 'data_line',
    list: genRegions(),
  },
  Routes: {
    menuTitle: 'Routes',
    list: {},
    singularString: 'Route',
    paramKey: 'routes',
    icon: 'route',
    isSingleSelect: true,
    tKey: 'Routes',
  },
  Status: {
    menuTitle: 'Status',
    list: generateStatusList(),
    paramKey: 'active',
    icon: 'status',
    isSingleSelect: true,
    tKey: 'Status',
  },
  Timezones: {
    menuTitle: 'Timezones',
    list: genTimezoneList(),
    singularString: 'Timezone',
    paramKey: 'timezone',
    icon: 'clock',
    isSingleSelect: true,
    tKey: 'Timezones',
  },
  Providers: {
    menuTitle: 'Providers',
    list: {},
    singularString: 'Providers',
    paramKey: 'Providers',
    isSingleSelect: false,
    getList: (session, _) => getProviderNames(session),
  },
  'Segment Type': {
    menuTitle: 'Segment Type',
    list: {},
    singularString: 'SegmentType',
    paramKey: 'Segment Type',
    isSingleSelect: false,
    getList: (session, _) => getSegmentTypes(session),
  },
  'Use Cases': {
    menuTitle: 'Use Cases',
    list: generateUseCaseList(),
    singularString: 'UseCases',
    paramKey: 'Use Cases',
    isSingleSelect: false,
  },
  IsRequested: {
    menuTitle: 'IsRequested',
    singularString: 'Status',
    paramKey: 'IsRequested',
    isSingleSelect: true,
    list: generateRequestedList(),
  },
} as Record<FilterPillKey, IFilterPill>;

export type FilterPillIconKey =
  | 'bidders'
  | 'channels'
  | 'clock'
  | 'data'
  | 'data_line'
  | 'deals'
  | 'device_type'
  | 'route'
  | 'status'
  | 'tiers'
  | 'top_sort';

export interface IFilterPillIcon {
  labelStyle?: CSS.Properties; // css for label
  style?: CSS.Properties; // css for img tag
  SvgComponent: any; // icon component
}

// warning: editing this will affect all places the icon is used at
export const FilterPillIcons = {
  bidders: {
    style: {
      paddingTop: '2px',
    },
    SvgComponent: BiddersPillIcon,
  },
  channels: {
    style: {},
    SvgComponent: ChannelsPillIcon,
  },
  clock: {
    style: {},
    SvgComponent: ClockPillIcon,
  },
  data: {
    style: {
      height: '11px',
      paddingRight: '1px',
    },
    SvgComponent: DataPillIcon,
  },
  data_line: {
    style: { paddingTop: '2px' },
    SvgComponent: DataLinePillIcon,
  },
  deals: {
    style: {},
    SvgComponent: DealsPillIcon,
  },
  device_type: {
    style: { paddingTop: '2px', paddingRight: '2px' },
    SvgComponent: DeviceTypePillIcon,
  },
  route: {
    style: { paddingTop: '2px' },
    SvgComponent: RoutePillIcon,
  },
  status: {
    style: {
      paddingRight: '2px',
    },
    SvgComponent: StatusPillIcon,
  },
  tiers: {
    style: {},
    SvgComponent: TiersPillIcon,
  },
  top_sort: {
    style: {},
    SvgComponent: TopSortPillIcon,
  },
} as Record<FilterPillIconKey, IFilterPillIcon>;

export const areFiltersEmpty = (filters: IFiltersState) => {
  if (isEmpty(filters)) {
    return true;
  }
  return Object.values(filters).every((filter) => isEmpty(filter.list));
};
